import { useState } from "react";
import logger from "@/utils/logger";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Navigate } from "react-router-dom";
import { LogIn, UserPlus, AlertCircle, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { normalizeMatricula } from "@/utils/matriculaUtils";
import { MatriculaInput } from "@/components/shared/MatriculaInput";
import { useSelfSignupSettings, useCreateSignupRequest, useCheckSignupEligibility, useLogSignupAttempt } from "@/hooks/useSignupRequests";
import { useSystemTheme } from "@/hooks/useSystemTheme";
import { generateGradient } from "@/lib/sidebarThemes";

const AuthPage = () => {
  const { signIn, user, loading } = useAuth();
  const { loginTheme, isLoading: isLoadingTheme } = useSystemTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const { data: selfSignupSettings } = useSelfSignupSettings();
  const createSignupRequest = useCreateSignupRequest();
  const checkEligibility = useCheckSignupEligibility();
  const logAttempt = useLogSignupAttempt();
  
  const isSignupDisabled = selfSignupSettings && !(selfSignupSettings as any).enabled;

  // Verificar se há parâmetro reset na URL para exibir página de reset
  const urlParams = new URLSearchParams(window.location.search);
  const isPasswordReset = urlParams.get('reset') === 'true';

  // Generate dynamic login gradient style
  const loginGradientStyle = {
    background: generateGradient(
      loginTheme.gradient_from,
      loginTheme.gradient_via,
      loginTheme.gradient_to
    )
  };

  // Redirect if already authenticated
  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const credential = (formData.get("credential") as string).trim();
    const password = formData.get("password") as string;

    try {
      // Detectar se é email ou matrícula
      const isEmail = credential.includes('@');
      let loginEmail = credential;

      if (!isEmail) {
        // Normalizar matrícula: remover zeros à esquerda e caracteres especiais
        const normalizedMatricula = normalizeMatricula(credential);
        
        // Buscar email pela matrícula usando a função do banco
        const { data, error: lookupError } = await supabase
          .rpc('get_email_by_matricula', { p_matricula: normalizedMatricula });

        if (lookupError || !data) {
          setError("Matrícula não encontrada no sistema.");
          setIsLoading(false);
          return;
        }
        loginEmail = data;
      }

      // Verificar se é usuário God Mode via Edge Function (bypassa RLS)
      const { data: godModeCheck } = await supabase.functions.invoke('godmode-auth/check-godmode', {
        body: { email: loginEmail }
      });

      if (godModeCheck?.isGodMode) {
        // Tentar autenticação God Mode via Edge Function
        const { data: godModeResult, error: godModeError } = await supabase.functions.invoke('godmode-auth/login', {
          body: { email: loginEmail, password }
        });

        if (godModeError) {
          setError("Erro ao autenticar no God Mode.");
          setIsLoading(false);
          return;
        }

        if (godModeResult?.token && godModeResult?.user) {
          // Salvar token e usuário God Mode
          localStorage.setItem('godmode_token', godModeResult.token);
          localStorage.setItem('godmode_user', JSON.stringify(godModeResult.user));
          // Redirecionar para dashboard God Mode
          window.location.href = '/godmode/dashboard';
          return;
        }

        // Se chegou aqui, houve erro na autenticação God Mode
        setError(godModeResult?.error || "Credenciais God Mode inválidas.");
        setIsLoading(false);
        return;
      }

      // Fluxo normal para usuários comuns
      const { error } = await signIn(loginEmail, password);

      if (error) {
        setError(error.message === "Invalid login credentials" 
          ? "Credenciais inválidas. Verifique seu email/matrícula e senha." 
          : error.message);
      }
    } catch (err: any) {
      setError("Erro ao fazer login. Tente novamente.");
    }

    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const fullName = formData.get("fullName") as string;
    const nomeGuerra = formData.get("nomeGuerra") as string;
    const telefone = formData.get("telefone") as string;
    const unidadeLotacao = formData.get("unidadeLotacao") as string;
    // Normalizar matrícula: remover zeros à esquerda e caracteres especiais
    const matricula = normalizeMatricula(formData.get("matricula") as string);
    const justification = formData.get("justification") as string;

    // Validação de campos obrigatórios
    if (!fullName?.trim()) {
      setError("Nome completo é obrigatório.");
      setIsLoading(false);
      return;
    }

    if (!matricula?.trim()) {
      setError("Matrícula é obrigatória.");
      setIsLoading(false);
      return;
    }

    // Validação de email obrigatório
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!email?.trim()) {
      setError("Email é obrigatório.");
      setIsLoading(false);
      return;
    }
    if (!emailRegex.test(email.trim())) {
      setError("Por favor, insira um email válido.");
      setIsLoading(false);
      return;
    }

    try {
      // Registrar tentativa de cadastro
      await logAttempt.mutateAsync({ email });

      // Verificar elegibilidade (bloqueios, solicitações pendentes, rate limiting)
      await checkEligibility.mutateAsync({ email, matricula: matricula || undefined });

      // Se passou nas verificações, criar a solicitação
      await createSignupRequest.mutateAsync({
        email,
        full_name: fullName,
        nome_guerra: nomeGuerra || undefined,
        telefone: telefone || undefined,
        unidade_lotacao: unidadeLotacao || undefined,
        matricula: matricula || undefined,
        justification: justification || undefined,
        role_requested: 'PADRAO'
      });
      
      setSuccess("Solicitação enviada! Se aprovada, sua senha inicial será 123456 (1 a 6). Você poderá alterá-la no primeiro acesso.");
    } catch (error: any) {
      logger.error('Signup error:', error);
      
      if (error.message === "BLOCKED") {
        setError("Usuário bloqueado. Você não pode enviar novas solicitações. Entre em contato com os administradores se necessário.");
      } else if (error.message === "PENDING") {
        setError("Você já possui uma solicitação pendente de aprovação. Aguarde o resultado antes de enviar uma nova solicitação.");
      } else if (error.message === "RATE_LIMIT") {
        const details = error.details || {};
        const waitMinutes = details.wait_minutes || 15;
        const nextAttempt = details.next_attempt_at 
          ? new Date(details.next_attempt_at).toLocaleString('pt-BR')
          : `${waitMinutes} minutos`;
        
        setError(`Muitas tentativas de cadastro. Você deve aguardar até ${nextAttempt} antes de tentar novamente.`);
      } else {
        setError(error.message || "Erro ao enviar solicitação. Tente novamente mais tarde.");
      }
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth?reset=true`
      });

      if (error) throw error;

      setSuccess(`Email de recuperação enviado para ${resetEmail}. Verifique sua caixa de entrada.`);
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error: any) {
      setError(error.message || "Erro ao enviar email de recuperação.");
    }

    setIsLoading(false);
  };

  return (
    <main 
      className="min-h-screen flex items-center justify-center p-4"
      style={loginGradientStyle}
    >
      <div className="w-full max-w-2xl">
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-white mb-1">Gestão Aretê</h1>
          <p className="text-white/80 text-sm">Gestão Estratégica e Contratações</p>
        </div>

        <Card className="bg-background/95 backdrop-blur-sm border-border">
          <CardHeader className="text-center">
            <h2 className="font-semibold tracking-tight text-2xl text-foreground">Acesso ao Sistema</h2>
            <CardDescription className="text-muted-foreground">
              Entre com sua conta ou crie uma nova
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={isPasswordReset ? "reset" : "signin"} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin" className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Entrar
                </TabsTrigger>
                <TabsTrigger value="signup" className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Cadastrar
                </TabsTrigger>
              </TabsList>

              {error && (
                <Alert className="mb-4 border-red-200 bg-red-50 text-red-800">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-credential">Email ou Matrícula</Label>
                    <Input
                      id="signin-credential"
                      name="credential"
                      type="text"
                      placeholder="seu.email@exemplo.com ou somente números"
                      className="bg-muted border-border"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Para login por matrícula, digite somente os números. Letras na matrícula devem ser substituídas por 0 (zero); mantenha os zeros à esquerda.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Senha</Label>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Sua senha"
                        className="bg-muted border-border pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                   <Button
                     type="submit"
                     className="w-full bg-primary hover:bg-primary/90"
                     disabled={isLoading}
                   >
                     {isLoading ? "Entrando..." : "Entrar"}
                   </Button>
                   
                   <div className="text-center mt-4 hidden">
                     <Button
                       type="button"
                       variant="link"
                       onClick={() => setShowForgotPassword(true)}
                       className="text-sm text-muted-foreground hover:text-primary p-0"
                     >
                       Esqueci minha senha
                     </Button>
                   </div>
                 </form>
               </TabsContent>

              <TabsContent value="signup">
                {isSignupDisabled ? (
                  <div className="space-y-4">
                    <Alert className="border-orange-200 bg-orange-50 text-orange-800">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Auto-cadastro desabilitado</strong><br />
                        Entre em contato com {(selfSignupSettings as any)?.admin_name} para solicitar acesso:<br />
                        📞 {(selfSignupSettings as any)?.admin_phone}<br />
                        📍 {(selfSignupSettings as any)?.admin_location}
                      </AlertDescription>
                    </Alert>
                    
                    <div className="p-4 border rounded-lg bg-muted/50">
                      <h4 className="font-medium mb-2">Ou envie uma solicitação online:</h4>
                      <form onSubmit={handleSignUp} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="signup-name">Nome Completo *</Label>
                            <Input
                              id="signup-name"
                              name="fullName"
                              type="text"
                              placeholder="Seu nome completo"
                              className="bg-background"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <MatriculaInput
                              id="signup-matricula"
                              name="matricula"
                              placeholder="Somente números (ex: 123456)"
                              className="bg-background"
                              required
                              label="Matrícula *"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">Email *</Label>
                          <Input
                            id="signup-email"
                            name="email"
                            type="email"
                            placeholder="seu.email@exemplo.com"
                            className="bg-background"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="signup-nomeguerra">Nome de Guerra</Label>
                          <Input
                            id="signup-nomeguerra"
                            name="nomeGuerra"
                            type="text"
                            placeholder="Ex: Silva"
                            className="bg-background"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="signup-telefone">Telefone</Label>
                            <Input
                              id="signup-telefone"
                              name="telefone"
                              type="tel"
                              placeholder="(11) 99999-9999"
                              className="bg-background"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="signup-unidade">Unidade de Lotação</Label>
                            <Input
                              id="signup-unidade"
                              name="unidadeLotacao"
                              type="text"
                              placeholder="Ex: SGTI"
                              className="bg-background"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-justification">Justificativa para acesso</Label>
                          <Textarea
                            id="signup-justification"
                            name="justification"
                            placeholder="Descreva brevemente por que precisa acessar o sistema"
                            className="bg-background"
                            rows={3}
                          />
                        </div>
                        
                        <p className="text-xs text-muted-foreground">* Campos obrigatórios</p>
                        
                        <Button
                          type="submit"
                          className="w-full bg-secondary hover:bg-secondary/90"
                          disabled={isLoading}
                        >
                          {isLoading ? "Enviando solicitação..." : "Enviar Solicitação"}
                        </Button>
                      </form>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSignUp} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="signup-name">Nome Completo *</Label>
                        <Input
                          id="signup-name"
                          name="fullName"
                          type="text"
                          placeholder="Seu nome completo"
                          className="bg-muted border-border"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <MatriculaInput
                          id="signup-matricula"
                          name="matricula"
                          placeholder="Somente números (ex: 123456)"
                          className="bg-muted border-border"
                          required
                          label="Matrícula *"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="signup-email">Email *</Label>
                        <Input
                          id="signup-email"
                          name="email"
                          type="email"
                          placeholder="seu.email@exemplo.com"
                          className="bg-muted border-border"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="signup-nomeguerra">Nome de Guerra</Label>
                        <Input
                          id="signup-nomeguerra"
                          name="nomeGuerra"
                          type="text"
                          placeholder="Ex: Silva"
                          className="bg-muted border-border"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="signup-telefone">Telefone</Label>
                        <Input
                          id="signup-telefone"
                          name="telefone"
                          type="tel"
                          placeholder="(11) 99999-9999"
                          className="bg-muted border-border"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="signup-unidade">Unidade de Lotação</Label>
                        <Input
                          id="signup-unidade"
                          name="unidadeLotacao"
                          type="text"
                          placeholder="Ex: SGTI"
                          className="bg-muted border-border"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="signup-justification">Justificativa para acesso</Label>
                      <Textarea
                        id="signup-justification"
                        name="justification"
                        placeholder="Descreva brevemente por que precisa acessar o sistema"
                        className="bg-muted border-border"
                        rows={2}
                      />
                    </div>

                    <p className="text-xs text-muted-foreground">* Campos obrigatórios. Se aprovado, sua senha inicial será 123456 (1 a 6).</p>

                    <Button
                      type="submit"
                      className="w-full bg-secondary hover:bg-secondary/90"
                      disabled={isLoading}
                    >
                      {isLoading ? "Enviando solicitação..." : "Enviar Solicitação"}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Modal de Esqueci Minha Senha */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Recuperar Senha</DialogTitle>
              <DialogDescription>
                Digite seu email para receber um link de recuperação de senha.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="seu.email@exemplo.com"
                  required
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                    setError(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-primary hover:bg-primary/90"
                >
                  {isLoading ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
};

export default AuthPage;
